import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useId, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CHECKOUT_RULE_TYPES, screenPath, toPriceText } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { DeliveryCalendar } from '../components/DeliveryCalendar'
import { deliveryOptionLabel } from '../checkout/deliveryOptions'
import { ActionBar, Screen, TopBar } from '../design'
import { shippingMethods, useDemo, type ShippingMethod } from '../demo'
import { useCheckoutRuleOptions } from '../hooks/useCheckoutRuleOptions'
import { SetCheckoutRuleError, useSetCheckoutRule } from '../hooks/useSetCheckoutRule'
import { usePresentationContext } from '../hooks/usePresentationContext'

/** Same default express fee the cart charges. See `CartPage`. */
const SHIPPING_METHODS = shippingMethods()

const MISSING_DATE_MESSAGE = 'Selecciona una fecha de entrega para continuar.'
/** The server refused the value against its fresh list: expired, not wrong. */
const STALE_DATE_MESSAGE =
  'Esa fecha ya no está disponible. Las fechas se actualizaron — elige otra.'
const SAVE_FAILED_MESSAGE = 'No pudimos guardar tu fecha de entrega. Intenta de nuevo.'
const OPTIONS_FAILED_MESSAGE = 'No pudimos cargar las fechas de entrega.'

/** The design's success green, for a fee of zero. */
const FREE_FG = '#0B6B4C'

/**
 * Where a pickup order is collected.
 *
 * Hardcoded exactly as the design hardcodes it. There is no distribution-centre
 * endpoint and no field for it on `Distributor`, so inventing one in
 * `fixtures.ts` would be inventing a second placeholder for the same nothing.
 */
const PICKUP_LOCATION = {
  title: 'Centro de distribución',
  address: 'Av. Industria 240, Parque Norte',
  hours: 'Lun–Sáb · 8:00–17:00 · listo en 3 h',
} as const

const MethodOption = ({
  method,
  selected,
  feeText,
}: {
  method: ShippingMethod
  selected: boolean
  feeText: string
}) => (
  <FormControlLabel
    value={method.id}
    control={<Radio />}
    disableTypography
    sx={(theme) => ({
      m: 0,
      p: 1.5,
      gap: 1,
      alignItems: 'center',
      bgcolor: YALO_SEMANTIC.bgSurface,
      border: `1.5px solid ${selected ? theme.palette.primary.main : YALO_SEMANTIC.borderSubtle}`,
      borderRadius: `${YALO_RADIUS.md + 2}px`,
      // The label wrapper does not stretch on its own, so the fee would sit
      // against the method name instead of at the far edge of the card.
      '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
    })}
    label={
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {method.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {method.detail}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: method.fee > 0 ? 'text.primary' : FREE_FG }}
        >
          {feeText}
        </Typography>
      </Stack>
    }
  />
)

/**
 * Shipping method and delivery date.
 *
 * The dates are real: `getCheckoutRule(DELIVERY_DATES)` asks the client's own
 * system, live, through the storefront's add-on, and `setCheckoutRuleSession`
 * persists the choice onto the session — which is what carries it into the
 * order. The choice is *also* kept in `DemoContext` so payment and
 * confirmation can read it back without another round trip.
 *
 * The shipping methods are still `shippingMethods()` fixtures: no rule type
 * exists for them (scoping doc, out of scope).
 *
 * The primary action stays enabled when no date has been chosen, and reports
 * why instead. A disabled button is a dead end: it gives the shopper nothing to
 * act on and screen readers skip it, so the one person who most needs the
 * reason is the one least likely to find it.
 */
export const DeliveryPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const presentation = usePresentationContext()
  const {
    deliveryDate,
    shippingMethodId,
    chooseShippingMethod,
    chooseDeliveryDate,
  } = useDemo()
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const methodsLabelId = useId()

  const {
    options,
    loading: optionsLoading,
    error: optionsError,
    refetch,
  } = useCheckoutRuleOptions(CHECKOUT_RULE_TYPES.deliveryDates)
  const { setRule } = useSetCheckoutRule()

  const dateTokens = useMemo(() => options.map((option) => option.data), [options])

  const method =
    SHIPPING_METHODS.find((entry) => entry.id === shippingMethodId) ?? SHIPPING_METHODS[0]!

  const feeTextOf = (entry: ShippingMethod): string =>
    entry.fee > 0 ? `+${toPriceText(entry.fee, presentation)}` : 'Gratis'

  const onContinue = async () => {
    if (!method.needsDate) return navigate(screenPath('payment', { slug, sessionId }))
    if (deliveryDate === null) return setError(MISSING_DATE_MESSAGE)

    setError('')
    setSaving(true)
    try {
      // The whole option goes back exactly as offered — the server recomputes
      // the list and compares by strict equality, so sending the bare date
      // string is rejected even while that date is still on offer. The picker
      // is keyed on the token, so recover the option it came from here.
      const offered = options.find((option) => option.data === deliveryDate)
      // Gone from a list that has since refreshed: the same state the server
      // would report, so take the same recovery rather than sending a guess.
      if (!offered) throw new SetCheckoutRuleError('not-offered')

      await setRule(CHECKOUT_RULE_TYPES.deliveryDates, offered)
      return navigate(screenPath('payment', { slug, sessionId }))
    } catch (failure) {
      const stale =
        failure instanceof SetCheckoutRuleError && failure.reason === 'not-offered'
      if (stale) {
        // The list legitimately moved between read and write (a cutoff, a
        // rollover). The chosen date is gone; showing it as still selected
        // would invite the same rejection again.
        chooseDeliveryDate(null)
        await refetch()
      }
      return setError(stale ? STALE_DATE_MESSAGE : SAVE_FAILED_MESSAGE)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Screen testId="delivery-page">
      <TopBar title="Entrega" backTo={screenPath('cart', { slug, sessionId })} />

      <Stack spacing={2} sx={{ flex: 1, px: 2, py: 2 }}>
        <Box>
          <Typography
            id={methodsLabelId}
            variant="overline"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            Método de envío
          </Typography>
          <RadioGroup
            aria-labelledby={methodsLabelId}
            value={shippingMethodId}
            onChange={(event) => {
              chooseShippingMethod(event.target.value as ShippingMethod['id'])
              // The message was about the previous method's requirements.
              setError('')
            }}
            sx={{ gap: 1 }}
          >
            {SHIPPING_METHODS.map((entry) => (
              <MethodOption
                key={entry.id}
                method={entry}
                selected={entry.id === shippingMethodId}
                feeText={feeTextOf(entry)}
              />
            ))}
          </RadioGroup>
        </Box>

        {method.needsDate && (
          <>
            {optionsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={28} aria-label="Cargando fechas de entrega" />
              </Box>
            )}

            {!optionsLoading && optionsError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => void refetch()}>
                    Reintentar
                  </Button>
                }
              >
                {OPTIONS_FAILED_MESSAGE}
              </Alert>
            )}

            {!optionsLoading && !optionsError && (
              <DeliveryCalendar
                options={dateTokens}
                selected={deliveryDate}
                onSelect={(token) => {
                  chooseDeliveryDate(token)
                  setError('')
                }}
              />
            )}

            {deliveryDate !== null && (
              <Typography
                role="status"
                variant="body2"
                fontWeight={600}
                sx={(theme) => ({
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.dark,
                  borderRadius: `${YALO_RADIUS.md}px`,
                  px: 1.75,
                  py: 1.25,
                })}
              >
                {`Llega el ${deliveryOptionLabel(deliveryDate)}`}
              </Typography>
            )}
          </>
        )}

        {!method.needsDate && (
          <Box
            sx={{
              bgcolor: YALO_SEMANTIC.bgSurface,
              border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
              borderRadius: `${YALO_RADIUS.lg}px`,
              p: 2,
            }}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {PICKUP_LOCATION.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {PICKUP_LOCATION.address}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {PICKUP_LOCATION.hours}
            </Typography>
          </Box>
        )}
      </Stack>

      <ActionBar>
        <Stack spacing={1.5}>
          {error !== '' && <Alert severity="error">{error}</Alert>}
          {/* Disabled only while the save is in flight — double-submitting a
              rule write is how a shopper ends up racing themselves. */}
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={() => void onContinue()}
            disabled={saving}
          >
            {saving ? 'Guardando…' : 'Continuar al pago'}
          </Button>
        </Stack>
      </ActionBar>
    </Screen>
  )
}
